import React from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar, Checkbox, Chip, Divider, Stack, Typography } from '@mui/material';
import { Email, Phone } from '@mui/icons-material';
import { CONTACT_AVATAR_COLORS } from '../../../constants';
import { getContactId, getContactInitials } from '../../../lib';
import { DealBuilderContact } from '../../../types';

interface Props {
  contact: DealBuilderContact;
  checked: boolean;
  disabled?: boolean;
  isPrimary?: boolean;
  avatarIndex: number;
  onToggle?: () => void;
}

export const ContactRow = ({ contact, checked, disabled, isPrimary, avatarIndex, onToggle }: Props) => {
  const history = useHistory();

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <Checkbox checked={checked} disabled={disabled} onChange={onToggle} />
      <Avatar sx={{ bgcolor: CONTACT_AVATAR_COLORS[avatarIndex % CONTACT_AVATAR_COLORS.length], color: 'black' }}>
        <Typography variant="subtitle2">{getContactInitials(contact.fullName)}</Typography>
      </Avatar>

      <Stack flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="subtitle2"
            noWrap
            component="span"
            onClick={contact.ownerPgId ? () => history.push(`/ownerPgDetails/${contact.ownerPgId}`) : undefined}
            sx={{
              color: contact.ownerPgId ? 'primary.main' : 'text.primary',
              cursor: contact.ownerPgId ? 'pointer' : 'default',
              '&:hover': contact.ownerPgId ? { textDecoration: 'underline' } : undefined,
            }}
          >
            {contact.fullName}
          </Typography>
          {isPrimary && (
            <Chip
              label={<Typography variant="caption">Primary</Typography>}
              size="small"
              sx={{ bgcolor: 'grey.200', color: 'grey.700', borderRadius: 1 }}
            />
          )}
        </Stack>

        {contact.title && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {contact.title}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" divider={<Divider orientation="vertical" flexItem />} gap={1.5}>
          {contact.email && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Email fontSize="inherit" sx={{ fontSize: '16px', color: 'grey.500' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
                Email:
              </Typography>
              <Typography variant="body2" noWrap>
                {contact.email}
              </Typography>
            </Stack>
          )}
          {contact.phone && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Phone fontSize="inherit" sx={{ fontSize: '16px', color: 'grey.500' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
                Phone:
              </Typography>
              <Typography variant="body2" noWrap>
                {contact.phone}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
